import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { TaxYearInfo } from '../services/payment.types';
import { useTaxReportService } from '../services/tax-report.service';
import { useUserPlanStatus } from './useUserPlanStatus';

/**
 * Custom hook to get available tax years from tax reports
 * This hook provides a consistent way to access tax years across the application
 */
export const useTaxYears = () => {
    const { currentCtUser } = useAuth();
    const taxReportService = useTaxReportService();
    const { isLoadingUserPlans, isPurchased, getPurchasedPackage } = useUserPlanStatus();

    // Fetch tax reports
    const { data: taxReports = [], isLoading: isLoadingTaxReports } = useQuery({
        queryKey: ['taxReports'],
        queryFn: async () => {
            return taxReportService.getAll();
        },
        enabled: !!currentCtUser,
        gcTime: 0,
        staleTime: 0,
    });

    // Transform tax reports into tax years using useMemo
    const availableTaxYears = useMemo(() => {
        if (!taxReports || taxReports.length === 0) {
            return [];
        }

        // Group reports by year and sum transaction counts
        const yearMap = new Map<string, TaxYearInfo>();

        taxReports.forEach((report) => {
            const year = report.tax_year;
            const purchased = isPurchased(year);
            const transactionCount = report.transaction_count || 0;

            if (!yearMap.has(year)) {
                yearMap.set(year, {
                    year,
                    transactions: transactionCount,
                    purchased,
                });
            }
        });

        const years = Array.from(yearMap.values());
        return years.sort((a, b) => parseInt(b.year) - parseInt(a.year));
    }, [taxReports, isPurchased, getPurchasedPackage]);

    return {
        availableTaxYears,
        isLoadingTaxYears: isLoadingTaxReports || isLoadingUserPlans,
        taxReports: taxReports || [],
    };
};
